import type { FairUsePersonRow } from "@/api/admin/fair-use"
import type { LocalAiLeadMapping } from "@/api/admin-api"

import { fairUseRowLeadInfo } from "./fair-use-people-utils"
import { fairUseDisplayName } from "./fair-use-utils"

export type PeopleFilters = {
  search: string
  plan: string[] | null
  company: string[] | null
  /** Companies dropped while `company` is null. Missing means none. */
  companyExcluded?: string[]
  department: string[] | null
  /** Departments dropped while `department` is null. Missing means none. */
  departmentExcluded?: string[]
  usage: string[] | null
  lead: string[] | null
  group: string[] | null
  minimumSpend: string
  maximumSpend: string
  sort: string
}
export const EMPTY_PEOPLE_FILTERS: PeopleFilters = {
  search: "",
  plan: null,
  company: null,
  companyExcluded: [],
  department: null,
  departmentExcluded: [],
  usage: null,
  lead: null,
  group: null,
  minimumSpend: "",
  maximumSpend: "",
  sort: "name-asc",
}

export function selectPeople(
  rows: FairUsePersonRow[],
  filters: PeopleFilters,
  context: {
    members: Set<string>
    leads?: LocalAiLeadMapping[]
    groupLabels?: Map<string, string[]>
  }
) {
  const search = filters.search.trim().toLocaleLowerCase()
  const minimum = filters.minimumSpend.trim() ? Number(filters.minimumSpend) : null
  const maximum = filters.maximumSpend.trim() ? Number(filters.maximumSpend) : null
  const matches = rows.filter((row) => {
    if (
      search &&
      ![row.profile?.name, row.profile?.email, row.user]
        .join(" ")
        .toLocaleLowerCase()
        .includes(search)
    )
      return false
    if (filters.plan !== null && !filters.plan.includes(row.tier ?? "standard")) return false
    if (filters.company !== null && !filters.company.includes(row.profile?.company ?? ""))
      return false
    if (
      filters.company === null &&
      (filters.companyExcluded ?? []).includes(row.profile?.company ?? "")
    )
      return false
    if (filters.department !== null && !filters.department.includes(row.profile?.department ?? ""))
      return false
    if (
      filters.department === null &&
      (filters.departmentExcluded ?? []).includes(row.profile?.department ?? "")
    )
      return false
    if (filters.group !== null && !context.members.has(row.user.toLowerCase())) return false
    const lead = fairUseRowLeadInfo(row, context.leads)
    const leadKnown = row.is_lead !== undefined || context.leads !== undefined
    if (
      filters.lead !== null &&
      !filters.lead.some(
        (value) =>
          (value === "lead" && lead.isLead) ||
          (value === "assigned" && lead.leadOf.length > 0) ||
          (value === "none" && leadKnown && !lead.isLead && lead.leadOf.length === 0) ||
          (value === "unknown" && !leadKnown)
      )
    )
      return false
    if (
      filters.usage !== null &&
      !filters.usage.some(
        (value) =>
          (value === "used" && row.has_usage) ||
          (value === "unused" && !row.has_usage) ||
          (value === "pending" && row.pending_request !== null) ||
          (value === "near" && (row.status === "warn" || row.status === "downgrade")) ||
          (value === "blocked" && row.status === "block")
      )
    )
      return false
    if (minimum !== null && (row.cost_sek === null || row.cost_sek < minimum)) return false
    if (maximum !== null && (row.cost_sek === null || row.cost_sek > maximum)) return false
    return true
  })
  const descending = filters.sort.endsWith("-desc")
  const sortKey = filters.sort.replace(/-(asc|desc)$/, "")
  const textValue = (row: FairUsePersonRow) => {
    if (sortKey === "plan") return row.tier ?? "standard"
    if (sortKey === "company") return row.profile?.company ?? ""
    if (sortKey === "department") return row.profile?.department ?? ""
    if (sortKey === "group")
      return context.groupLabels?.get(row.user.toLowerCase())?.join(", ") ?? ""
    if (sortKey === "lead") {
      const lead = fairUseRowLeadInfo(row, context.leads)
      return lead.isLead ? fairUseDisplayName(row) : lead.leadOf.map((item) => item.name).join(", ")
    }
    return fairUseDisplayName(row)
  }
  return matches.sort((a, b) => {
    const tie =
      fairUseDisplayName(a).localeCompare(fairUseDisplayName(b), undefined, {
        sensitivity: "base",
        numeric: true,
      }) || a.user.localeCompare(b.user)
    if (sortKey === "cost") {
      if (a.cost_sek === null) return b.cost_sek === null ? tie : 1
      if (b.cost_sek === null) return -1
      return (descending ? b.cost_sek - a.cost_sek : a.cost_sek - b.cost_sek) || tie
    }
    const aValue = textValue(a),
      bValue = textValue(b)
    if (!aValue) return bValue ? 1 : tie
    if (!bValue) return -1
    const comparison = aValue.localeCompare(bValue, undefined, {
      sensitivity: "base",
      numeric: true,
    })
    return (descending ? -comparison : comparison) || tie
  })
}

export function repeatedPeopleCursor(pages: { cursor: string | null }[]) {
  const cursors = pages.map((page) => page.cursor).filter(Boolean)
  return new Set(cursors).size !== cursors.length
}
